function escapeHtml (str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default {
  name: 'st-apexchart',
  inheritAttrs: false,
  props: {
    type: {
      type: String,
      default: 'line'
    },
    height: {
      type: [String, Number],
      default: undefined
    },
    width: {
      type: [String, Number],
      default: undefined
    },
    options: {
      type: Object,
      default: function () {
        return {}
      }
    },
    series: {
      type: Array,
      default: function () {
        return []
      }
    },
    legendResetKey: {
      type: [String, Number, Boolean],
      default: null
    },
    useStableLegendToggle: {
      type: Boolean,
      default: true
    },
    tooltipTotalLabel: {
      type: String,
      default: null
    },
    tooltipExtraHtmlByIndex: {
      type: Array,
      default: function () {
        return []
      }
    },
    disableRangeSlider: {
      type: Boolean,
      default: false
    },
    maxVisiblePoints: {
      type: Number,
      default: 52
    },
    showDownloadButton: {
      type: Boolean,
      default: true
    },
    downloadLabel: {
      type: String,
      default: 'CSV Download'
    },
    downloadType: {
      type: String,
      default: 'text/csv;charset=utf-8'
    },
    downloadFileName: {
      type: String,
      default: 'report.csv'
    },
    onDownload: {
      type: Function,
      default: null
    },
    downloadDisabled: {
      type: Boolean,
      default: false
    },
    downloadButtonAttributes: {
      type: Object,
      default: function () {
        return {
          small: true,
          color: 'primary',
          outline: true
        }
      }
    }
  },
  data () {
    return {
      hiddenSeriesNames: {},
      viewStartIndex: 0,
      apexRefreshKey: 0
    }
  },
  computed: {
    safeOptions () {
      return this.options && typeof this.options === 'object' ? this.options : {}
    },
    safeSeries () {
      return Array.isArray(this.series) ? this.series : []
    },
    totalDataPoints () {
      const opts = this.safeOptions
      const categories = opts.xaxis && Array.isArray(opts.xaxis.categories) ? opts.xaxis.categories : []
      if (categories.length > 0) { return categories.length }
      const series = this.safeSeries
      if (series.length > 0 && series[0] && Array.isArray(series[0].data)) {
        return series[0].data.length
      }
      return 0
    },
    rangeSliderActive () {
      return !this.disableRangeSlider && this.totalDataPoints > this.maxVisiblePoints
    },
    sliderMax () {
      if (!this.rangeSliderActive) { return 0 }
      return Math.max(0, this.totalDataPoints - this.maxVisiblePoints)
    },
    clampedViewStart () {
      if (!this.rangeSliderActive) { return 0 }
      const start = this.viewStartIndex
      if (start < 0) { return 0 }
      if (start > this.sliderMax) { return this.sliderMax }
      return start
    },
    viewSlice () {
      if (!this.rangeSliderActive) {
        return { start: 0, end: this.totalDataPoints }
      }
      return {
        start: this.clampedViewStart,
        end: Math.min(this.clampedViewStart + this.maxVisiblePoints, this.totalDataPoints)
      }
    },
    panStep () {
      return Math.max(1, Math.floor(this.maxVisiblePoints / 2))
    },
    canRenderDownloadButton () {
      return this.showDownloadButton && !!this.onDownload
    },
    resolvedSeries () {
      const useToggle = this.useStableLegendToggle
      const needsSlice = this.rangeSliderActive
      if (!useToggle && !needsSlice) {
        return this.safeSeries
      }

      const hiddenNames = this.hiddenSeriesNames
      const slice = this.viewSlice
      return this.safeSeries.map(function (item) {
        const base = item && typeof item === 'object' ? item : {}
        const name = base.name
        const hidden = useToggle && !!(name && hiddenNames[name])
        const sourceData = Array.isArray(base.data) ? base.data : []
        let data = hidden
          ? sourceData.map(function () { return null })
          : sourceData.slice()

        if (needsSlice) {
          data = data.slice(slice.start, slice.end)
        }

        return Object.assign({}, base, {
          data: data
        })
      })
    },
    resolvedOptions () {
      const useToggle = this.useStableLegendToggle
      const needsSlice = this.rangeSliderActive

      if (!useToggle && !needsSlice) {
        return this.safeOptions
      }

      const self = this
      const incomingOptions = this.safeOptions
      const incomingXaxis = incomingOptions.xaxis && typeof incomingOptions.xaxis === 'object' ? incomingOptions.xaxis : {}

      if (!useToggle) {
        const xaxis = needsSlice ? Object.assign({}, incomingXaxis, {
          categories: (Array.isArray(incomingXaxis.categories) ? incomingXaxis.categories : []).slice(this.viewSlice.start, this.viewSlice.end)
        }) : incomingXaxis
        return Object.assign({}, incomingOptions, { xaxis: xaxis })
      }

      const incomingChart = incomingOptions.chart && typeof incomingOptions.chart === 'object' ? incomingOptions.chart : {}
      const incomingEvents = incomingChart.events && typeof incomingChart.events === 'object' ? incomingChart.events : {}
      const incomingLegend = incomingOptions.legend && typeof incomingOptions.legend === 'object' ? incomingOptions.legend : {}
      const incomingOnItemClick = incomingLegend.onItemClick && typeof incomingLegend.onItemClick === 'object' ? incomingLegend.onItemClick : {}
      const incomingTooltip = incomingOptions.tooltip && typeof incomingOptions.tooltip === 'object' ? incomingOptions.tooltip : {}
      const incomingTooltipY = incomingTooltip.y && typeof incomingTooltip.y === 'object' ? incomingTooltip.y : {}
      const incomingValueFormatter = typeof incomingTooltipY.formatter === 'function' ? incomingTooltipY.formatter : null

      const chart = Object.assign({}, incomingChart, {
        events: Object.assign({}, incomingEvents, {
          mounted: function (chartContext, config) {
            self.syncLegendAppearance(chartContext)
            if (typeof incomingEvents.mounted === 'function') {
              incomingEvents.mounted(chartContext, config)
            }
          },
          updated: function (chartContext, config) {
            self.syncLegendAppearance(chartContext)
            if (typeof incomingEvents.updated === 'function') {
              incomingEvents.updated(chartContext, config)
            }
          },
          legendClick: function (chartContext, seriesIndex, config) {
            const seriesName = self.resolveSeriesName(chartContext, seriesIndex)
            if (seriesName) {
              self.toggleSeriesVisibility(seriesName)
              setTimeout(function () {
                self.syncLegendAppearance(chartContext)
              }, 50)
            }

            if (typeof incomingEvents.legendClick === 'function') {
              incomingEvents.legendClick(chartContext, seriesIndex, config)
            }
          }
        })
      })

      const legend = Object.assign({}, incomingLegend, {
        onItemClick: Object.assign({}, incomingOnItemClick, {
          toggleDataSeries: false
        })
      })

      const tooltip = Object.assign({}, incomingTooltip, {
        custom: function (opts) {
          const dataPointIndex = opts.dataPointIndex
          const hoveredSeriesIndex = typeof opts.seriesIndex === 'number' ? opts.seriesIndex : -1
          const w = opts.w
          const categories = w && w.config && w.config.xaxis && Array.isArray(w.config.xaxis.categories)
            ? w.config.xaxis.categories : []
          const categoryLabel = dataPointIndex >= 0 && dataPointIndex < categories.length
            ? String(categories[dataPointIndex]) : ''
          const colors = w && w.globals && Array.isArray(w.globals.colors) ? w.globals.colors : []
          const propSeries = self.safeSeries
          const hiddenNames = self.hiddenSeriesNames
          const totalLabel = self.tooltipTotalLabel
          const tooltipExtraByIndex = Array.isArray(self.tooltipExtraHtmlByIndex) ? self.tooltipExtraHtmlByIndex : []

          const actualDataIndex = self.rangeSliderActive
            ? (self.clampedViewStart + dataPointIndex)
            : dataPointIndex

          let grossTotal = 0
          for (let i = 0; i < propSeries.length; i += 1) {
            const item = propSeries[i] && typeof propSeries[i] === 'object' ? propSeries[i] : {}
            const data = Array.isArray(item.data) ? item.data : []
            let rawValue = actualDataIndex >= 0 && actualDataIndex < data.length ? data[actualDataIndex] : 0
            if (rawValue === null || rawValue === undefined) { rawValue = 0 }
            if (totalLabel) {
              grossTotal += Number(rawValue) || 0
            }
          }

          let html = '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 11px;">' +
            escapeHtml(categoryLabel)

          if (totalLabel) {
            const formattedTotalInTitle = incomingValueFormatter
              ? incomingValueFormatter(grossTotal, { series: opts.series, seriesIndex: -1, dataPointIndex: actualDataIndex, w: w })
              : String(grossTotal)

            html += '<div style="font-family: Helvetica, Arial, sans-serif; font-size: 11px; font-weight: 700; margin-top: 2px;">' +
              escapeHtml(totalLabel) + ': ' + escapeHtml(String(formattedTotalInTitle)) +
              '</div>'
          }

          html += '</div>'

          const hasValidHoveredSeries = hoveredSeriesIndex >= 0 && hoveredSeriesIndex < propSeries.length
          const displaySeriesIndexes = hasValidHoveredSeries ? [hoveredSeriesIndex] : []

          for (let index = 0; index < displaySeriesIndexes.length; index += 1) {
            const i = displaySeriesIndexes[index]
            const item = propSeries[i] && typeof propSeries[i] === 'object' ? propSeries[i] : {}
            const name = item.name ? String(item.name) : ''
            const data = Array.isArray(item.data) ? item.data : []
            let rawValue = actualDataIndex >= 0 && actualDataIndex < data.length ? data[actualDataIndex] : 0
            if (rawValue === null || rawValue === undefined) { rawValue = 0 }

            if (name && hiddenNames[name]) { continue }

            const formattedValue = incomingValueFormatter
              ? incomingValueFormatter(rawValue, { series: opts.series, seriesIndex: i, dataPointIndex: actualDataIndex, w: w })
              : String(rawValue)

            const color = i < colors.length ? colors[i] : '#999'

            html += '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: ' + (i + 1) + '; display: flex;">'
            html += '<span class="apexcharts-tooltip-marker" style="background-color: ' + escapeHtml(color) + ';"></span>'
            html += '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 11px;">'
            html += '<div class="apexcharts-tooltip-y-group">'
            html += '<span class="apexcharts-tooltip-text-y-label">' + escapeHtml(name) + ': </span>'
            html += '<span class="apexcharts-tooltip-text-y-value">' + escapeHtml(String(formattedValue)) + '</span>'
            html += '</div></div></div>'
          }

          const extraTooltipHtml = actualDataIndex >= 0 && actualDataIndex < tooltipExtraByIndex.length
            ? String(tooltipExtraByIndex[actualDataIndex] || '')
            : ''

          if (extraTooltipHtml) {
            html += '<div class="apexcharts-tooltip-extras" style="font-family: Helvetica, Arial, sans-serif; font-size: 11px; margin-top: 4px; padding: 0 8px;">' + extraTooltipHtml + '</div>'
          }

          return html
        }
      })

      const xaxis = needsSlice ? Object.assign({}, incomingXaxis, {
        categories: (Array.isArray(incomingXaxis.categories) ? incomingXaxis.categories : []).slice(this.viewSlice.start, this.viewSlice.end)
      }) : incomingXaxis

      return Object.assign({}, incomingOptions, {
        chart: chart,
        legend: legend,
        tooltip: tooltip,
        xaxis: xaxis
      })
    }
  },
  watch: {
    legendResetKey () {
      this.clearHiddenSeries()
    },
    series () {
      this.pruneHiddenSeries()
      this.refreshApexChart()
    },
    options () {
      this.refreshApexChart()
    },
    totalDataPoints: {
      handler () {
        this.scrollToEnd()
      },
      immediate: true
    }
  },
  methods: {
    refreshApexChart () {
      this.apexRefreshKey += 1
    },
    scrollToEnd () {
      if (this.rangeSliderActive) {
        this.viewStartIndex = this.sliderMax
      } else {
        this.viewStartIndex = 0
      }
    },
    panLeft () {
      this.viewStartIndex = Math.max(0, this.viewStartIndex - this.panStep)
    },
    panRight () {
      this.viewStartIndex = Math.min(this.sliderMax, this.viewStartIndex + this.panStep)
    },
    clearHiddenSeries () {
      this.hiddenSeriesNames = {}
    },
    resolveSeriesName (chartContext, seriesIndex) {
      const propSeries = this.safeSeries
      if (Array.isArray(propSeries) && seriesIndex >= 0 && seriesIndex < propSeries.length) {
        const propSeriesName = propSeries[seriesIndex] && propSeries[seriesIndex].name
        if (propSeriesName) {
          return String(propSeriesName)
        }
      }

      const globals = chartContext && chartContext.w ? chartContext.w.globals : null
      if (!globals || !Array.isArray(globals.seriesNames)) {
        return null
      }

      return globals.seriesNames[seriesIndex] || null
    },
    toggleSeriesVisibility (seriesName) {
      if (!seriesName) {
        return
      }

      if (this.hiddenSeriesNames[seriesName]) {
        this.$delete(this.hiddenSeriesNames, seriesName)
      } else {
        this.$set(this.hiddenSeriesNames, seriesName, true)
      }
    },
    pruneHiddenSeries () {
      if (!this.useStableLegendToggle) {
        return
      }

      const knownSeriesNames = {}
      const source = this.safeSeries
      for (let i = 0; i < source.length; i += 1) {
        const series = source[i]
        const name = series && series.name ? String(series.name) : null
        if (name) {
          knownSeriesNames[name] = true
        }
      }

      Object.keys(this.hiddenSeriesNames).forEach((name) => {
        if (!knownSeriesNames[name]) {
          this.$delete(this.hiddenSeriesNames, name)
        }
      })
    },
    syncLegendAppearance (chartContext) {
      if (!chartContext || !chartContext.el || !chartContext.el.querySelectorAll) {
        return
      }

      const globals = chartContext.w ? chartContext.w.globals : null
      const seriesNames = globals && Array.isArray(globals.seriesNames) ? globals.seriesNames : []
      const legendItems = chartContext.el.querySelectorAll('.apexcharts-legend-series')

      for (let i = 0; i < legendItems.length; i += 1) {
        const item = legendItems[i]
        const name = i < seriesNames.length ? seriesNames[i] : null
        const isHidden = name ? !!this.hiddenSeriesNames[name] : false

        item.style.opacity = isHidden ? '0.45' : '1'
        if (isHidden) {
          if (item.classList && !item.classList.contains('apexcharts-inactive-legend')) {
            item.classList.add('apexcharts-inactive-legend')
          }
        } else if (item.classList && item.classList.contains('apexcharts-inactive-legend')) {
          item.classList.remove('apexcharts-inactive-legend')
        }
      }
    }
  }
}
